/**
 * @file
 * Final Results: Final & Day table
 *
 * A single component that can render the day and final block variant of
 * Final Results.
 */

import range from 'lodash/range';
import * as React from 'react';
import 'url-search-params-polyfill';

// Components
import ApiTable from '../../components/ApiTable/ApiTable';
import BassComponent from '../../components/BassComponent/BassComponent';

// Config
import meta from './meta';

// Util
import {
  buildFinalResultsDayTableRow,
  buildFinalResultsFinalTableRow,
  buildFinalResultsMiniTableRow,
} from './processing';

// Types
import { IWithTournamentType, TournamentId, TournamentType } from '../../types';

export enum FinalResultsVariant {
  FINAL = 'final',
  DAY = 'day',
  MINI = 'mini',
}

interface IProps extends IWithTournamentType {
  // Tournament ID, e.g. 1225
  tid: TournamentId;
  // Day (only used in day blockVariant)
  day?: number;
  // Total days of tournament
  days?: number;
  // Select change
  selectChange?: (event: React.FormEvent<HTMLSelectElement>) => void;
  // Mini is smaller table used in tournament standings
  mini?: boolean;
  // NOTE: gets Tournament type: 0 (co) or 1 (pro, default) from extends
  // type?: TournamentType;
}
interface IState {
  blockVariant: FinalResultsVariant;
}
export class FinalResults extends React.Component<IProps, IState> {
  public static defaultProps = {
    // Block defaults to PRO (1). CO (0) must be explicitly passed in
    type: TournamentType.PRO,
  };

  public state = {
    // Block has "day", "final", "mini" variants; affect API route, row builder, etc
    blockVariant: this.props.mini
      ? FinalResultsVariant.MINI
      : this.props.day
      ? FinalResultsVariant.DAY
      : FinalResultsVariant.FINAL,
  };

  // Local property w/ type. Actual bound function assigned in constructor()
  public tableRowBuilder: (row: any, index: number) => any; // @TODO: Proper type signature

  // Used in apiUrl() below to concatenate Final Results day/final API route
  private finalApiBase = '/wp-json/data/v1/tournament/final-results';
  private dailyApiBase = '/wp-json/data/v1/tournament/daily-results';
  private apiBase = this.props.day ? this.dailyApiBase : this.finalApiBase;

  constructor(props: IProps) {
    super(props);

    const { day, mini } = props;

    this.tableRowBuilder = mini
      ? buildFinalResultsMiniTableRow
      : day
      ? buildFinalResultsDayTableRow
      : buildFinalResultsFinalTableRow;
  }

  public render() {
    const options = this.props.days
      ? [
          { val: 0, text: 'Final Results' },
          ...range(1, this.props.days + 1).map(day => ({
            val: day,
            text: `Day ${day}`, // tslint:disable-line:object-literal-sort-keys
          })),
        ]
      : [];

    return (
      <div data-testid="final-results-table">
        <BassComponent
          meta={{
            ...meta,
            ...{ nickname: `Final Results: ${this.state.blockVariant}` },
          }}
        >
          {this.props.days && (
            <select
              name="results_page"
              className="form-select"
              onChange={this.props.selectChange}
              value={this.props.day}
              data-testid="final-results-select"
            >
              {options.map(({ val, text }) => (
                <option key={val} value={val}>
                  {text}
                </option>
              ))}
            </select>
          )}

          <ApiTable
            apiUri={this.apiUri()}
            tableRowFormatter={this.tableRowBuilder}
            rootCssClass={
              this.props.day ? 'final-results-day' : 'final-results'
            }
          />
        </BassComponent>
      </div>
    );
  }

  // Return /tid/type or /tid/type/day depending if day exists as props
  private apiUri = () => {
    const { tid, type, day, mini } = this.props;
    // No need to boolean check optional day. Spreads empty array if undefined.
    const currentDay = day ? `&day=${day}` : '';
    const limit = mini ? `&limit=5` : '';
    return `${this.apiBase}?class=${type === 0 ? 'coanglers' : 'anglers'}&tournament_id=${tid}${currentDay}${limit}`;
  };
}

/**
 * Change URL query by value sent in
 * @param event
 */
const selectChange = (event: React.FormEvent<HTMLSelectElement>) => {
  const selectDay = event.currentTarget.value;
  let params = selectDay ? `?day=${selectDay}` : '';
  // Get the pro parameter from the URL.
  const urlParams = new URLSearchParams(window.location.search);
  const pro = urlParams.get('pro');
  // If pro is not null, add it to the params.
  if (pro) {
    params += `&pro=${pro}`;
  }

  window.location.href = `${window.location.pathname}${params}`;
};

/**
 * Check for day in URL query to override prop day
 */
export const HOCFinalResults: React.FunctionComponent<IProps> = props => {
  const day = new URLSearchParams(window.location.search).get('day');

  // Provide *optional* new day prop
  const newProps = {
    ...props,
    // Day from url
    ...(day && { day: parseInt(day, 10) }),
    // Select change function
    ...(props.days && { selectChange }),
  };

  return <FinalResults {...newProps} />;
};

export default HOCFinalResults;
